/**
 * Filter Bar Component
 *
 * Provides comprehensive filtering controls for the projects table.
 * Includes global search, collapsible advanced filters, and CSV export.
 * Follows the same pattern as ExpenseList filters.
 *
 * @module components/projects/FilterBar
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Form, Button, InputGroup, Badge, Collapse } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { getAllStatuses } from '../common/ProjectStatusBadge';
import { supabase } from '../../services/supabase';
import { exportProjectsToCSV } from '../../services/projectService';
import './FilterBar.css';

/**
 * FilterBar Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onFilterChange - Callback when filters change
 * @param {Array} props.projects - Projects array for CSV export
 */
const FilterBar = ({ filters, onFilterChange, projects = [] }) => {
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  // Ref for debounce timer
  const searchTimeoutRef = useRef(null);

  /**
   * Handle search input change with debounce (300ms delay)
   */
  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      onFilterChange('search', value);
    }, 300);
  }, [onFilterChange]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Fetch clients for dropdown
  const { data: clientsData } = useQuery({
    queryKey: ['clients-filter'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('id, company_name, primary_contact_name')
        .order('company_name');

      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

  // Fetch technicians for assignee filter
  const { data: employeesData } = useQuery({
    queryKey: ['technicians'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, role')
        .in('role', ['admin', 'technician'])
        .order('full_name');

      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

  // Get all available statuses
  const statuses = getAllStatuses();

  // Computed: count active filters (excluding search)
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.status) count++;
    if (filters.client_id) count++;
    if (filters.assigned_to) count++;
    if (filters.date_from) count++;
    if (filters.date_to) count++;
    return count;
  }, [filters.status, filters.client_id, filters.assigned_to, filters.date_from, filters.date_to]);

  const hasActiveFilters = activeFiltersCount > 0 || filters.search;

  /**
   * Handle clear all filters
   */
  const handleClearFilters = () => {
    onFilterChange('search', '');
    onFilterChange('status', '');
    onFilterChange('client_id', '');
    onFilterChange('assigned_to', '');
    onFilterChange('date_from', '');
    onFilterChange('date_to', '');
    setSearchInput('');
  };

  /**
   * Toggle filters panel
   */
  const toggleFiltersPanel = () => setIsFiltersExpanded((prev) => !prev);

  // Handle CSV export
  const handleExportCSV = () => {
    if (!projects || projects.length === 0) {
      alert('No hay proyectos para exportar');
      return;
    }

    const csvContent = exportProjectsToCSV(projects);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `proyectos_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="project-search-filter mb-4">
      {/* Global search bar + filter toggle + export */}
      <div className="project-global-search">
        <InputGroup className="flex-grow-1">
          <InputGroup.Text>
            <i className="bi bi-search"></i>
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Buscar proyectos..."
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            aria-label="Buscar en proyectos"
          />
        </InputGroup>
        <Button
          variant={activeFiltersCount > 0 ? 'primary' : 'outline-secondary'}
          onClick={toggleFiltersPanel}
          aria-expanded={isFiltersExpanded}
          aria-controls="project-advanced-filters"
          className="filter-toggle-btn"
        >
          <i className={`bi bi-chevron-${isFiltersExpanded ? 'up' : 'down'}`}></i>
          <span className="d-none d-md-inline ms-1">Filtros</span>
          {activeFiltersCount > 0 && (
            <Badge bg="light" text="dark" pill className="filter-count-badge">
              {activeFiltersCount}
            </Badge>
          )}
        </Button>
        <Button
          variant="outline-success"
          onClick={handleExportCSV}
          disabled={!projects || projects.length === 0}
          aria-label="Exportar a CSV"
          className="export-btn"
        >
          <i className="bi bi-file-earmark-spreadsheet"></i>
          <span className="d-none d-md-inline ms-1">Exportar a CSV</span>
        </Button>
      </div>

      {/* Collapsible advanced filters panel */}
      <Collapse in={isFiltersExpanded}>
        <div id="project-advanced-filters">
          <div className="advanced-filters-panel">
            <Row className="g-2 g-md-3">
              {/* Status Filter */}
              <Col xs={12} sm={6} lg={2}>
                <Form.Label className="small text-muted mb-1">Estado</Form.Label>
                <Form.Select
                  value={filters.status || ''}
                  onChange={(e) => onFilterChange('status', e.target.value)}
                  aria-label="Filtrar por estado"
                  size="sm"
                >
                  <option value="">Todos los estados</option>
                  {statuses.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              {/* Client Filter */}
              <Col xs={12} sm={6} lg={3}>
                <Form.Label className="small text-muted mb-1">Cliente</Form.Label>
                <Form.Select
                  value={filters.client_id || ''}
                  onChange={(e) => onFilterChange('client_id', e.target.value)}
                  aria-label="Filtrar por cliente"
                  size="sm"
                >
                  <option value="">Todos los clientes</option>
                  {Array.isArray(clientsData) && clientsData.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.company_name || client.primary_contact_name}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              {/* Assignee Filter */}
              <Col xs={12} sm={6} lg={3}>
                <Form.Label className="small text-muted mb-1">Responsable</Form.Label>
                <Form.Select
                  value={filters.assigned_to || ''}
                  onChange={(e) => onFilterChange('assigned_to', e.target.value)}
                  aria-label="Filtrar por responsable"
                  size="sm"
                >
                  <option value="">Todos los responsables</option>
                  {Array.isArray(employeesData) && employeesData.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.full_name}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              {/* Date From */}
              <Col xs={6} sm={6} lg={2}>
                <Form.Label className="small text-muted mb-1">Desde</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.date_from || ''}
                  onChange={(e) => onFilterChange('date_from', e.target.value)}
                  aria-label="Fecha desde"
                  size="sm"
                />
              </Col>

              {/* Date To */}
              <Col xs={6} sm={6} lg={2}>
                <Form.Label className="small text-muted mb-1">Hasta</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.date_to || ''}
                  onChange={(e) => onFilterChange('date_to', e.target.value)}
                  aria-label="Fecha hasta"
                  size="sm"
                />
              </Col>
            </Row>

            {/* Clear filters button */}
            {hasActiveFilters && (
              <div className="d-flex justify-content-end mt-3">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleClearFilters}
                  className="clear-filters-btn"
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Limpiar filtros
                  {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
                </Button>
              </div>
            )}
          </div>
        </div>
      </Collapse>
    </div>
  );
};

FilterBar.propTypes = {
  filters: PropTypes.shape({
    search: PropTypes.string,
    status: PropTypes.string,
    client_id: PropTypes.string,
    assigned_to: PropTypes.string,
    date_from: PropTypes.string,
    date_to: PropTypes.string,
  }).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  projects: PropTypes.array,
};

export default FilterBar;
